import styled from 'styled-components';
import theme from 'theme/theme';
import media from 'utils/MediaQueries';
import {
	PAGE_MAX,
	PAGE_PAD,
	PAGE_PAD_MOBILE,
	riseIn,
} from 'components/Section/styled';

/**
 * Shared reading layout for numbered legal documents (Privacy Policy, Terms
 * of Service). Same idiom as a course catalogue's contents page: a sticky
 * numbered index alongside the reading column, section numbers and dates in
 * the mono utility face, tabular data (the subprocessor table) in mono too.
 * Add a new legal page by building its SECTIONS array + JSX against these
 * primitives rather than duplicating the layout.
 */
const Wrapper = styled.div`
	width: 100%;
	display: flex;
	flex-direction: column;
	align-items: center;
`;

/**
 * Intro sits closer to the TopBar than the marketing pages' hero spacing —
 * this is a document to get into, not a pitch to scroll past.
 */
const Intro = styled.div`
	width: 100%;
	max-width: ${PAGE_MAX};
	padding: 0 ${PAGE_PAD};
	margin-top: 96px;
	margin-bottom: 40px;

	${media.tablet`
		margin-top: 76px;
		padding: 0 ${PAGE_PAD_MOBILE};
	`};
`;

const IntroEyebrow = styled.span`
	display: block;
	font-family: ${theme.monoFont};
	color: ${theme.primary1};
	font-size: 12px;
	font-weight: 500;
	letter-spacing: 2px;
	text-transform: uppercase;
	margin-bottom: 16px;
`;

const IntroTitle = styled.h1`
	font-family: ${theme.headerFont};
	color: ${theme.chalk};
	font-size: 40px;
	line-height: 1.2;
	margin: 0 0 16px 0;

	${media.phone`
		font-size: 29px;
	`};
`;

const IntroLead = styled.p`
	max-width: 640px;
	margin: 0 0 22px 0;
	font-size: 16.5px;
	line-height: 1.68;
	color: ${theme.chalk};
	opacity: 0.7;
`;

const IntroMeta = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: 4px 20px;
	font-family: ${theme.monoFont};
	font-size: 12.5px;
	color: ${theme.chalk};
	opacity: 0.55;
	font-variant-numeric: tabular-nums;
`;

/**
 * Two-column body: a sticky numbered index on the left (the same idiom as a
 * course catalogue's contents page) and the reading column on the right.
 * Collapses to a single column with the index up top on tablet/phone.
 */
const Layout = styled.div`
	width: 100%;
	max-width: ${PAGE_MAX};
	padding: 0 ${PAGE_PAD} 100px;
	display: grid;
	grid-template-columns: 216px minmax(0, 700px);
	gap: 56px;
	justify-content: center;
	${riseIn};

	${media.tablet`
		grid-template-columns: minmax(0, 1fr);
		padding: 0 ${PAGE_PAD_MOBILE} 70px;
		gap: 0;
	`};
`;

const TocNav = styled.nav`
	position: sticky;
	top: 28px;
	align-self: start;

	${media.tablet`
		position: static;
		margin-bottom: 30px;
		border-bottom: 1px solid ${theme.rule};
		padding-bottom: 22px;
	`};
`;

const TocLabel = styled.span`
	display: block;
	font-family: ${theme.monoFont};
	color: ${theme.chalk};
	opacity: 0.45;
	font-size: 11px;
	font-weight: 500;
	letter-spacing: 2px;
	text-transform: uppercase;
	margin-bottom: 14px;
`;

const TocList = styled.ol`
	list-style: none;
	margin: 0;
	padding: 0;
`;

const TocItem = styled.li`
	margin: 0;
`;

const TocLink = styled.a`
	display: flex;
	gap: 10px;
	align-items: baseline;
	padding: 5px 0 5px 13px;
	border-left: 2px solid
		${(props) => (props.$active ? theme.primary1 : 'transparent')};
	font-size: 13.5px;
	line-height: 1.4;
	color: ${(props) => (props.$active ? theme.chalk : theme.chalk)};
	opacity: ${(props) => (props.$active ? 1 : 0.55)};
	font-weight: ${(props) => (props.$active ? 700 : 600)};
	text-decoration: none;
	cursor: pointer;
	transition: opacity 0.15s ease, border-color 0.15s ease;

	&:hover {
		opacity: 1;
	}
`;

const TocNum = styled.span`
	font-family: ${theme.monoFont};
	color: ${theme.primary1};
	font-size: 12px;
	flex-shrink: 0;
`;

const Main = styled.div`
	min-width: 0;
`;

const Article = styled.section`
	scroll-margin-top: 24px;
	padding-top: 38px;
	border-top: 1px solid ${theme.rule};
	margin-top: 38px;

	&:first-of-type {
		margin-top: 0;
		padding-top: 0;
		border-top: none;
	}
`;

const ArticleHeading = styled.h2`
	display: flex;
	align-items: baseline;
	gap: 10px;
	color: ${theme.chalk};
	font-size: 21px;
	font-weight: 700;
	line-height: 1.3;
	margin: 0 0 16px 0;
`;

const ArticleNum = styled.span`
	font-family: ${theme.monoFont};
	color: ${theme.primary1};
	font-size: 13px;
	font-weight: 500;
`;

const SubHeading = styled.h3`
	color: ${theme.chalk};
	font-size: 15.5px;
	font-weight: 700;
	margin: 26px 0 10px 0;
`;

const P = styled.p`
	margin: 0 0 16px 0;
	font-size: 15px;
	line-height: 1.75;
	color: ${theme.chalk};
	opacity: 0.72;

	&:last-child {
		margin-bottom: 0;
	}

	strong {
		opacity: 1;
		font-weight: 700;
	}
`;

const List = styled.ul`
	margin: 0 0 16px 0;
	padding-left: 20px;
	font-size: 15px;
	line-height: 1.75;
	color: ${theme.chalk};
	opacity: 0.72;

	li {
		margin-bottom: 8px;
	}

	li:last-child {
		margin-bottom: 0;
	}

	strong {
		opacity: 1;
		font-weight: 700;
	}
`;

const OrderedList = styled(List).attrs({ as: 'ol' })``;

/**
 * Same surface + spine language as the feature Item blocks on the marketing
 * pages — the established "here's something worth noticing" idiom, reused
 * for the contact callout instead of a feature blurb.
 */
const Callout = styled.div`
	background-color: rgba(255, 255, 255, 0.035);
	border-left: 3px solid ${theme.primary1};
	border-radius: 6px;
	padding: 16px 20px;
	margin: 0 0 16px 0;
	font-size: 14px;
	line-height: 1.7;
	color: ${theme.chalk};
	opacity: 0.85;

	strong {
		opacity: 1;
		font-weight: 700;
	}
`;

const TableWrap = styled.div`
	width: 100%;
	overflow-x: auto;
	margin: 0 0 16px 0;
	border: 1px solid ${theme.rule};
	border-radius: 6px;
`;

const Table = styled.table`
	width: 100%;
	min-width: 480px;
	border-collapse: collapse;
	font-family: ${theme.monoFont};
	font-size: 12.5px;
`;

const Th = styled.th`
	text-align: left;
	padding: 10px 16px;
	color: ${theme.chalk};
	opacity: 0.5;
	font-weight: 500;
	letter-spacing: 0.6px;
	text-transform: uppercase;
	border-bottom: 1px solid ${theme.ruleStrong};
	white-space: nowrap;
`;

const Td = styled.td`
	text-align: left;
	padding: 10px 16px;
	color: ${theme.chalk};
	opacity: 0.75;
	border-bottom: 1px solid ${theme.rule};
	vertical-align: top;
`;

const Address = styled.address`
	font-style: normal;
	font-family: ${theme.monoFont};
	font-size: 13.5px;
	line-height: 1.9;
	color: ${theme.chalk};
	opacity: 0.8;
`;

const BackToTop = styled.a`
	display: inline-flex;
	align-items: center;
	gap: 6px;
	margin-top: 56px;
	font-family: ${theme.monoFont};
	font-size: 12px;
	color: ${theme.chalk};
	opacity: 0.5;
	text-decoration: none;
	cursor: pointer;

	&:hover {
		opacity: 1;
		color: ${theme.primary1};
	}
`;

export {
	Wrapper,
	Intro,
	IntroEyebrow,
	IntroTitle,
	IntroLead,
	IntroMeta,
	Layout,
	TocNav,
	TocLabel,
	TocList,
	TocItem,
	TocLink,
	TocNum,
	Main,
	Article,
	ArticleHeading,
	ArticleNum,
	SubHeading,
	P,
	List,
	OrderedList,
	Callout,
	TableWrap,
	Table,
	Th,
	Td,
	Address,
	BackToTop,
};
